'use client';

import * as React from 'react';
import NextLink, { type LinkProps as NextLinkProps } from 'next/link';
import { useRouter } from 'next/navigation';

export interface LinkProps extends Omit<NextLinkProps, 'prefetch'> {
  /**
   * Extended prefetch behavior
   * - `true`: Prefetch on mount (default Next.js behavior)
   * - `false`: Never prefetch
   * - `'intent'`: Prefetch on hover or focus (intent-based)
   */
  prefetch?: boolean | 'intent';
  children?: React.ReactNode;
  className?: string;
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
  onFocus?: React.FocusEventHandler<HTMLAnchorElement>;
}

/**
 * Enhanced Link component with intent-based prefetching
 *
 * @example
 * ```tsx
 * // Standard prefetch (on mount)
 * <Link href="/about" prefetch>About</Link>
 *
 * // Intent-based prefetch (on hover/focus)
 * <Link href="/blog" prefetch="intent">Blog</Link>
 *
 * // No prefetch
 * <Link href="/contact" prefetch={false}>Contact</Link>
 * ```
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { href, prefetch = false, children, onMouseEnter, onFocus, ...props },
    ref
  ) => {
    const router = useRouter();
    const [hasPrefetched, setHasPrefetched] = React.useState(false);

    // Convert href to string for prefetch
    const hrefString = typeof href === 'string' ? href : href.pathname || '/';

    const handlePrefetch = React.useCallback(() => {
      if (prefetch === 'intent' && !hasPrefetched) {
        router.prefetch(hrefString);
        setHasPrefetched(true);
      }
    }, [prefetch, hasPrefetched, router, hrefString]);

    const handleMouseEnter = React.useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        handlePrefetch();
        onMouseEnter?.(event);
      },
      [handlePrefetch, onMouseEnter]
    );

    const handleFocus = React.useCallback(
      (event: React.FocusEvent<HTMLAnchorElement>) => {
        handlePrefetch();
        onFocus?.(event);
      },
      [handlePrefetch, onFocus]
    );

    // For standard boolean prefetch, pass it through to Next.js Link
    const nextPrefetch = prefetch === 'intent' ? false : prefetch;

    return (
      <NextLink
        ref={ref}
        href={href}
        prefetch={nextPrefetch}
        onMouseEnter={handleMouseEnter}
        onFocus={handleFocus}
        {...props}
      >
        {children}
      </NextLink>
    );
  }
);

Link.displayName = 'Link';
